'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';

import type { BadgeProps } from '@sola/ark-core/atoms/badge/badge.tsx';
import { NumberField } from '@sola/ark-core/molecules/number-field/number-field.tsx';
import type { RadioItemProps } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { RadioGroup } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import type { BadgeColors, FundOptionProps } from '@sola/schema/lib/schema.ts';
import { camelCase } from '@sola/utils/string.ts';

import type { FundQueryParams } from '../../../../utils/query-params.tsx';
import { useDonate } from '../donate-context.tsx';
import { DonateActionEnum, DonateFrequency } from '../donate.types.ts';
import { useDonateActions } from '../hooks/use-donate-actions.tsx';
import { useDonateValidation } from '../hooks/use-donate-validation.tsx';

export const DonateAmountSelector: React.FC<{
  className?: string;
  focusOnLoad?: boolean;
  alt?: boolean;
}> = ({ className, focusOnLoad = false, alt }) => {
  const { t } = useTranslation(['core', 'donations', 'skippy']);

  const { config, state, props, variant, fundParams, refs, dispatch } =
    useDonate();
  const { getCurrentAmount } = useDonateValidation();

  const {
    handleAmountSelect,
    handleOtherAmountChange,
    handleInputFocus,
    handleInputBlur,
  } = useDonateActions();

  const getRadioItemsForFrequency = (
    frequency: DonateFrequency,
  ): RadioItemProps[] => {
    const freq = camelCase(frequency) as keyof FundQueryParams;
    const defaultFreq = `default${frequency}` as keyof FundQueryParams;

    const suggestedAmounts =
      (fundParams?.[freq] as number[])
        ?.filter(
          (v) =>
            v >= config.donationLimits.min && v <= config.donationLimits.max,
        )
        ?.map((v) => {
          return {
            value: v,
            isDefault: v === fundParams?.[defaultFreq],
          } as FundOptionProps;
        }) ??
      props.fund?.[`suggestedAmounts${frequency}`] ??
      [];

    const items: RadioItemProps[] = [];
    if (suggestedAmounts.length > 1) {
      for (const amount of suggestedAmounts) {
        items.push({
          value: amount.value.toString(),
          text: t('numbers.currencyCustom', { amount: amount.value }),
          subText:
            variant === 'grid' && amount.label ? amount.label : undefined,
          badge: amount.badge
            ? ({
                label: amount.badge,
                color: 'positive' as BadgeColors,
              } as BadgeProps)
            : undefined,
          type: 'default',
        });
      }
    }
    items.push({
      value: 'other',
      type: 'other',
      text: t('donations:other'),
      element: (
        <NumberField
          key={`${frequency}-other`}
          inputRef={refs[frequency]}
          name={frequency}
          aria-label={t('donations:other')}
          placeholder="0"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleOtherAmountChange(e.target.value)
          }
          value={state.otherAmounts[frequency]}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          prefix="$"
        />
      ),
    });

    return items;
  };

  const items = getRadioItemsForFrequency(state.donateFrequency);

  if (!items.length) {
    return null;
  }

  const suggestedCount =
    (
      fundParams?.[
        camelCase(state.donateFrequency) as keyof FundQueryParams
      ] as number[]
    )?.length ||
    props.fund?.[`suggestedAmounts${state.donateFrequency}`]?.length ||
    0;

  const suggestedAmountOnly =
    config.enabledFrequencies.length === 0 || suggestedCount < 2;

  return (
    <div
      className={`DonateAmountSelector ${alt ? 'flex-1' : ''} ${className || ''}`}
    >
      {alt && (
        <div className="Donate__other-input">
          <NumberField
            name="multiple-other"
            label={t('donations:amount')}
            placeholder="0"
            value={state.otherAmounts[state.donateFrequency]}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              dispatch({
                type: DonateActionEnum.SET_OTHER_AMOUNT,
                payload: {
                  frequency: DonateFrequency.Monthly,
                  amount: e.target.value,
                },
              });
              dispatch({
                type: DonateActionEnum.SET_OTHER_AMOUNT,
                payload: {
                  frequency: DonateFrequency.OneTime,
                  amount: e.target.value,
                },
              });
            }}
            prefix="$"
            isFocused={focusOnLoad}
          />
        </div>
      )}

      {!alt && !suggestedAmountOnly && (
        <div
          className={cn(`Donate__items-${suggestedCount}`, {
            'Donate__other-input': state.otherSelected,
          })}
        >
          <RadioGroup
            key={`${state.donateFrequency}-amounts`}
            name={`${state.donateFrequency}-donateAmount`}
            aria-label={t('donations:chooseAmount')}
            items={items}
            variant={variant === 'grid' ? 'grid' : 'buttons'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (e.target.value === 'other') {
                handleOtherAmountChange(
                  state.otherAmounts[state.donateFrequency],
                );
              } else {
                handleAmountSelect(e.target.value);
              }
            }}
            value={state.selectedAmounts[state.donateFrequency].toString()}
          />
        </div>
      )}
      {!alt && suggestedAmountOnly && (
        <div className="Donate__other-suggestedAmount">
          {state.suggestedAmounts?.[state.donateFrequency] &&
            state.suggestedAmounts?.[state.donateFrequency] !== 'other' && (
              <p className="label-potent pb-snug-xl">
                {t('donations:suggestedGift', {
                  amount: state.suggestedAmounts[state.donateFrequency],
                })}
              </p>
            )}
          <NumberField
            key={`${state.donateFrequency}-other`}
            inputRef={refs[state.donateFrequency]}
            name={state.donateFrequency}
            aria-label={t('donations:other')}
            placeholder="0"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleOtherAmountChange(e.target.value)
            }
            value={state.otherAmounts[state.donateFrequency]}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            prefix="$"
            isFocused={focusOnLoad}
          />
        </div>
      )}
    </div>
  );
};
